import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { handleApiBadges } from "@/lib/badges";
import { Plus, Trash2, Pencil, Sparkles, Loader2, Download } from "lucide-react";
import { exportDietPlanPDF } from "@/lib/pdf-export";

const emptyMeal = { name: "", items: [""] };

export default function DietPlans() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ student_id: "", title: "", description: "", meals: [{ ...emptyMeal }], calories_target: "", protein_target: "", carbs_target: "", fat_target: "", water_target: "", steps_target: "" });
  const [loading, setLoading] = useState(false);

  // AI generation state
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [showAi, setShowAi] = useState(false);

  const isPro = user?.subscription?.plan_type === 'pro';

  const fetch = () => {
    Promise.all([
      api.get('/diet-plans').catch(() => ({ data: [] })),
      api.get('/students').catch(() => ({ data: [] })),
    ]).then(([plansRes, studentsRes]) => {
      setPlans(plansRes.data || []);
      setStudents(studentsRes.data || []);
    });
  };
  useEffect(() => { fetch(); }, []);

  const resetForm = () => setForm({ student_id: "", title: "", description: "", meals: [{ name: "", items: [""] }], calories_target: "", protein_target: "", carbs_target: "", fat_target: "", water_target: "", steps_target: "" });

  const openEdit = (plan) => {
    setEditing(plan.plan_id);
    setForm({
      student_id: plan.student_id, title: plan.title, description: plan.description || "",
      meals: plan.meals?.length ? plan.meals : [{ name: "", items: [""] }],
      calories_target: plan.calories_target || "", protein_target: plan.protein_target || "",
      carbs_target: plan.carbs_target || "", fat_target: plan.fat_target || "",
      water_target: plan.water_target ?? "", steps_target: plan.steps_target ?? ""
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.student_id) {
      toast.error("Sélectionnez un élève");
      return;
    }
    if (!form.title.trim()) {
      toast.error("Le titre est requis");
      return;
    }
    setLoading(true);
    const payload = {
      ...form,
      calories_target: form.calories_target ? Number(form.calories_target) : null,
      protein_target: form.protein_target ? Number(form.protein_target) : null,
      carbs_target: form.carbs_target ? Number(form.carbs_target) : null,
      fat_target: form.fat_target ? Number(form.fat_target) : null,
      water_target: form.water_target ? Number(form.water_target) : null,
      steps_target: form.steps_target ? Number(form.steps_target) : null,
    };
    try {
      if (editing) {
        await api.put(`/diet-plans/${editing}`, payload);
        toast.success("Plan mis à jour");
      } else {
        const res = await api.post('/diet-plans', payload);
        handleApiBadges(res, toast);
        toast.success("Plan créé");
      }
      setOpen(false); resetForm(); setEditing(null); fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce plan ?")) return;
    await api.delete(`/diet-plans/${id}`);
    toast.success("Plan supprimé");
    fetch();
  };

  const applyAiDiet = (ai) => {
    setForm(prev => ({
      ...prev,
      title: ai.title || prev.title,
      description: ai.description || prev.description,
      calories_target: ai.calories_target || prev.calories_target,
      protein_target: ai.protein_target || prev.protein_target,
      carbs_target: ai.carbs_target || prev.carbs_target,
      fat_target: ai.fat_target || prev.fat_target,
      water_target: ai.water_target ?? prev.water_target,
      steps_target: ai.steps_target ?? prev.steps_target,
      meals: ai.meals?.length ? ai.meals : prev.meals,
    }));
    setShowAi(false);
    setAiPrompt("");
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    try {
      const res = await api.post('/ai/generate-diet', { prompt: aiPrompt });
      applyAiDiet(res.data);
      toast.success("Plan généré par IA ! Vérifiez et ajustez si nécessaire.");
    } catch (err) {
      const detail = err.response?.data?.detail || "Erreur de génération IA";
      toast.error(detail.includes("Pro") ? "Génération IA réservée au plan Pro" : detail);
    } finally { setAiLoading(false); }
  };

  const handleAiAdjust = async () => {
    if (!aiPrompt.trim() || !editing) return;
    setAiLoading(true);
    try {
      const res = await api.post('/ai/adjust-diet', { plan_id: editing, prompt: aiPrompt });
      applyAiDiet(res.data);
      toast.success("Plan ajusté par IA ! Vérifiez et enregistrez.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'ajustement IA");
    } finally { setAiLoading(false); }
  };

  const updateMeal = (idx, field, value) => {
    const meals = [...form.meals];
    meals[idx] = { ...meals[idx], [field]: value };
    setForm({ ...form, meals });
  };

  const addMealItem = (mealIdx) => {
    const meals = [...form.meals];
    meals[mealIdx].items = [...meals[mealIdx].items, ""];
    setForm({ ...form, meals });
  };

  const updateMealItem = (mealIdx, itemIdx, value) => {
    const meals = [...form.meals];
    meals[mealIdx].items[itemIdx] = value;
    setForm({ ...form, meals });
  };

  const removeMealItem = (mealIdx, itemIdx) => {
    const meals = [...form.meals];
    meals[mealIdx].items = meals[mealIdx].items.filter((_, i) => i !== itemIdx);
    setForm({ ...form, meals });
  };

  const studentName = (id) => students.find(s => s.user_id === id)?.name || "N/A";

  return (
    <div data-testid="diet-plans-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Plans Diététiques</h1>
          <p className="text-slate-500 mt-1">{plans.length} plan{plans.length > 1 ? 's' : ''}</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { resetForm(); setEditing(null); setShowAi(false); setAiPrompt(""); } }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full" data-testid="create-diet-plan-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouveau plan
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="font-heading text-xl">{editing ? "Modifier le plan" : "Nouveau plan diététique"}</DialogTitle>
                {isPro && (
                  <Button type="button" variant="outline" size="sm" className="gap-1.5 text-purple-600 border-purple-200 hover:bg-purple-50" onClick={() => setShowAi(!showAi)}>
                    <Sparkles className="w-4 h-4" /> {editing ? "Ajuster avec IA" : "Générer avec IA"}
                  </Button>
                )}
              </div>
            </DialogHeader>

            {/* AI Generation Panel */}
            {showAi && (
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-100">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-semibold text-purple-900">Génération IA</span>
                  <Badge className="bg-purple-100 text-purple-700 text-[10px]">PRO</Badge>
                </div>
                <Textarea
                  value={aiPrompt}
                  onChange={e => setAiPrompt(e.target.value)}
                  placeholder={editing
                    ? "Décrivez les ajustements souhaités...\nEx: Mon élève a perdu 3kg, passe en maintien à 2500kcal"
                    : "Décrivez le profil de l'élève et ses objectifs...\nEx: Homme 25 ans, 80kg, objectif sèche, 2200kcal, 4 repas par jour, sans lactose"}
                  className="bg-white min-h-[80px] text-sm"
                />
                <Button
                  type="button"
                  disabled={aiLoading || !aiPrompt.trim()}
                  onClick={editing ? handleAiAdjust : handleAiGenerate}
                  className="mt-3 bg-purple-600 hover:bg-purple-700 text-white rounded-full gap-2 w-full"
                >
                  {aiLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> {editing ? "Ajustement en cours..." : "Génération en cours..."}</> : <><Sparkles className="w-4 h-4" /> {editing ? "Ajuster le plan" : "Générer le plan"}</>}
                </Button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label>Élève</Label>
                <Select value={form.student_id} onValueChange={v => setForm({ ...form, student_id: v })}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="diet-student-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    {students.map(s => <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Titre</Label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className="mt-1.5 h-11" data-testid="diet-title-input" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="mt-1.5" data-testid="diet-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Calories (kcal)</Label><Input type="number" value={form.calories_target} onChange={e => setForm({ ...form, calories_target: e.target.value })} className="mt-1.5 h-11" /></div>
                <div><Label>Protéines (g)</Label><Input type="number" value={form.protein_target} onChange={e => setForm({ ...form, protein_target: e.target.value })} className="mt-1.5 h-11" /></div>
                <div><Label>Glucides (g)</Label><Input type="number" value={form.carbs_target} onChange={e => setForm({ ...form, carbs_target: e.target.value })} className="mt-1.5 h-11" /></div>
                <div><Label>Lipides (g)</Label><Input type="number" value={form.fat_target} onChange={e => setForm({ ...form, fat_target: e.target.value })} className="mt-1.5 h-11" /></div>
                <div><Label>Eau (L / jour)</Label><Input type="number" step="0.1" min="0" value={form.water_target} onChange={e => setForm({ ...form, water_target: e.target.value })} placeholder="Ex: 2.5" className="mt-1.5 h-11" data-testid="diet-water-input" /></div>
                <div><Label>Pas / jour</Label><Input type="number" min="0" value={form.steps_target} onChange={e => setForm({ ...form, steps_target: e.target.value })} placeholder="Ex: 8000" className="mt-1.5 h-11" data-testid="diet-steps-input" /></div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Repas</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, meals: [...form.meals, { name: "", items: [""] }] })} data-testid="add-meal-btn">
                    <Plus className="w-3 h-3 mr-1" /> Repas
                  </Button>
                </div>
                {form.meals.map((meal, mi) => (
                  <div key={mi} className="p-3 bg-slate-50 rounded-xl mb-3">
                    <Input value={meal.name} onChange={e => updateMeal(mi, 'name', e.target.value)} placeholder="Ex: Petit-déjeuner" className="mb-2 h-10" />
                    {meal.items.map((item, ii) => (
                      <div key={ii} className="flex gap-2 mb-1.5">
                        <Input value={item} onChange={e => updateMealItem(mi, ii, e.target.value)} placeholder="Aliment" className="h-9 text-sm" />
                        <Button type="button" variant="ghost" size="icon" className="h-9 w-9 flex-shrink-0" onClick={() => removeMealItem(mi, ii)}><Trash2 className="w-3 h-3 text-slate-400" /></Button>
                      </div>
                    ))}
                    <Button type="button" variant="ghost" size="sm" onClick={() => addMealItem(mi)} className="text-xs text-blue-600">+ Ajouter un aliment</Button>
                  </div>
                ))}
              </div>

              <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full h-11" data-testid="submit-diet-plan-btn">
                {loading ? "Enregistrement..." : editing ? "Mettre à jour" : "Créer le plan"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 mt-6">
        {plans.length === 0 ? <p className="text-center text-slate-400 py-12">Aucun plan diététique</p> : plans.map(p => (
          <Card key={p.plan_id} className="border-slate-100 hover:shadow-sm transition-shadow" data-testid={`diet-plan-${p.plan_id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">{p.title}</h3>
                  <p className="text-sm text-slate-500 mt-1">{p.description}</p>
                  <div className="flex items-center gap-2 mt-3">
                    <Badge variant="secondary">{studentName(p.student_id)}</Badge>
                    {p.calories_target && <Badge variant="outline">{p.calories_target} kcal</Badge>}
                    {p.protein_target && <Badge variant="outline">{p.protein_target}g prot</Badge>}
                    {p.water_target && <Badge variant="outline">{p.water_target} L eau</Badge>}
                    {p.steps_target && <Badge variant="outline">{p.steps_target.toLocaleString("fr-BE")} pas</Badge>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => exportDietPlanPDF(p)} title="Exporter PDF"><Download className="w-4 h-4 text-slate-400" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-diet-${p.plan_id}`}><Pencil className="w-4 h-4 text-slate-400" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(p.plan_id)} data-testid={`delete-diet-${p.plan_id}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
